import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useViewMode } from "@/lib/ViewModeContext";
import {
  House, ListChecks, Printer, ChartLine, UsersThree, GearSix, SignOut,
  Warning, CloudArrowUp, ChartBar, Television, PaintBrush, Warehouse,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import CDLogo from "@/components/CDLogo";
import ThemeToggle from "@/components/ThemeToggle";
import ViewModeBadge from "@/components/ViewModeBadge";
import { ROLE_LABELS } from "@/lib/stages";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/orders", label: "Orders", icon: ListChecks },
  { to: "/labels", label: "Labels", icon: Printer },
  { to: "/paint", label: "Paint", icon: PaintBrush },
  { to: "/inventory", label: "Inventory", icon: Warehouse, adminOrManager: true },
  { to: "/reports", label: "Reports", icon: ChartLine },
  { to: "/productivity", label: "Productivity", icon: ChartBar, adminOnly: true },
  { to: "/wallboard", label: "Wallboard", icon: Television, adminOnly: true, newTab: true },
  { to: "/sync", label: "Sync", icon: CloudArrowUp, adminOrManager: true },
  { to: "/users", label: "Team", icon: UsersThree, adminOnly: true },
  { to: "/settings", label: "Settings", icon: GearSix, adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { viewMode, globalMode } = useViewMode() || {};
  const nav = useNavigate();

  const handleLogout = () => { logout(); nav("/login"); };

  const items = NAV.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (n.adminOrManager) return user?.role === "admin" || user?.role === "manager";
    return true;
  });
  const inTest = viewMode === "test";

  return (
    <div data-cd-app="1" className="min-h-screen flex bg-zinc-100 dark:bg-zinc-950 transition-colors">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-zinc-950 text-zinc-100 sticky top-0 h-screen border-r border-zinc-800" data-testid="app-sidebar">
        <div className="px-5 pt-6 pb-5 border-b border-zinc-800">
          <div className="flex items-center gap-2.5">
            <CDLogo className="w-10 h-10 shrink-0" />
            <div>
              <div className="font-heading font-black text-base leading-tight">Cabinet Depot</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-semibold">Desktop · Web</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {items.map((item) => (
            item.newTab ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
              >
                <item.icon size={20} weight="bold" />
                <span className="flex-1">{item.label}</span>
                <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">↗</span>
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#C8102E] text-white"
                      : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  }`
                }
              >
                <item.icon size={20} weight="bold" />
                <span>{item.label}</span>
              </NavLink>
            )
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center font-heading font-bold text-sm">
              {user?.name?.[0] || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
          <div className="px-2 pt-1 pb-2 space-y-2">
            <ViewModeBadge testId="view-mode-badge" />
            <ThemeToggle testId="theme-toggle" />
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-zinc-300 hover:bg-zinc-800 hover:text-white mt-1"
          >
            <SignOut size={18} weight="bold" className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {inTest && (
          <div
            data-testid="test-mode-banner"
            className="bg-yellow-400 text-yellow-950 px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 border-yellow-500 print:hidden"
          >
            <Warning size={18} weight="fill" />
            <span className="uppercase tracking-[0.2em] text-xs">
              You are viewing in TEST mode
              {globalMode === "live" ? " · team is on LIVE" : ""}
              {globalMode === "test" ? " · team default is TEST too" : ""}
            </span>
          </div>
        )}

        {/* Mobile top bar */}
        <div className="md:hidden bg-zinc-950 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 print:hidden">
          <div className="flex items-center gap-2">
            <CDLogo className="w-9 h-9 shrink-0" />
            <span className="font-heading font-black">Cabinet Depot</span>
          </div>
          <div className="flex items-center gap-1">
            <ViewModeBadge compact testId="view-mode-badge-mobile" />
            <ThemeToggle compact testId="theme-toggle-mobile" />
            <Button variant="ghost" onClick={handleLogout} className="text-zinc-300" data-testid="logout-button-mobile">
              <SignOut size={18} weight="bold" />
            </Button>
          </div>
        </div>

        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full mx-auto" data-testid="main-content">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden grid grid-cols-5 gap-0 bg-zinc-950 text-white sticky bottom-0 border-t border-zinc-800 print:hidden">
          {items.filter((i) => !i.newTab).slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-mobile-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase font-bold tracking-wider ${
                  isActive ? "text-[#C8102E]" : "text-zinc-400"
                }`
              }
            >
              <item.icon size={20} weight="bold" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
